import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Disclaimer } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CalendarDays, MessageCircle, Instagram, Globe, MapPin, Clock, Phone } from "lucide-react";

export default function ProfessionalProfile() {
  const nav = useNavigate();
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/my-therapist").then(({ data }) => setT(data)).catch(() => setT(false)); }, []);
  if (t === null) return <div className="h-40 animate-pulse bg-secondary rounded-2xl" />;
  if (t === false) return (
    <div>
      <PageHeader title="Meu Profissional" subtitle="Conheça seu hipnoterapeuta" />
      <Card className="p-10 text-center" data-testid="pp-no-therapist">
        <MapPin className="w-10 h-10 text-primary mx-auto mb-3" />
        <h3 className="font-heading text-lg font-medium">Você ainda não escolheu um profissional</h3>
        <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">Encontre hipnoterapeutas por região — inclusive quem atende online — para começar seu acompanhamento.</p>
        <Button className="rounded-full mt-5" onClick={() => nav("/app/encontrar")} data-testid="pp-find-btn"><CalendarDays className="w-4 h-4 mr-1" />Encontrar profissional</Button>
      </Card>
      <Disclaimer />
    </div>
  );
  const p = t.profile || {};
  const v = t.values || {};

  return (
    <div>
      <PageHeader title="Meu Profissional" subtitle="Conheça seu hipnoterapeuta" />
      <Card className="p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row gap-6">
          <Avatar className="w-24 h-24"><AvatarImage src={p.photo} /><AvatarFallback className="bg-primary/10 text-primary text-3xl">{t.name[0]}</AvatarFallback></Avatar>
          <div className="flex-1">
            <h2 className="font-heading text-2xl font-semibold">{t.name}</h2>
            {p.specialties && <div className="flex flex-wrap gap-2 mt-2">{p.specialties.split(",").map((s, i) => <Badge key={i} variant="secondary">{s.trim()}</Badge>)}</div>}
            {p.bio && <p className="text-muted-foreground mt-3">{p.bio}</p>}
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
              {p.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{p.city}</span>}
              {p.modality && <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{p.modality}</span>}
              {p.schedule && <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />{p.schedule}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              <Button className="rounded-full" onClick={() => nav("/app/consultas")} data-testid="pp-schedule-btn"><CalendarDays className="w-4 h-4 mr-1" />Agendar consulta</Button>
              {p.whatsapp && <a href={`https://wa.me/${p.whatsapp}`} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full" data-testid="pp-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1" />Falar com meu hipnoterapeuta</Button></a>}
              {p.instagram && <a href={`https://instagram.com/${p.instagram.replace("@", "")}`} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" className="rounded-full"><Instagram className="w-4 h-4" /></Button></a>}
              {p.site && <a href={p.site.startsWith("http") ? p.site : `https://${p.site}`} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" className="rounded-full"><Globe className="w-4 h-4" /></Button></a>}
            </div>
          </div>
        </div>
      </Card>

      {(v.first_consult || v.followup || v.online || v.other) && (
        <Card className="p-6 mt-4">
          <h3 className="font-heading font-medium mb-4">Valores dos atendimentos</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {v.first_consult ? <ValueItem label="Primeira consulta" value={v.first_consult} /> : null}
            {v.followup ? <ValueItem label="Acompanhamento" value={v.followup} /> : null}
            {v.online ? <ValueItem label="Consulta online" value={v.online} /> : null}
            {v.other ? <ValueItem label="Outros serviços" value={v.other} /> : null}
          </div>
        </Card>
      )}

      <Card className="mt-4 p-5 bg-primary/5 border-primary/20">
        <p className="text-sm">Precisa agendar sua próxima sessão? Para uma orientação personalizada, converse com seu hipnoterapeuta.</p>
      </Card>
      <Disclaimer />
    </div>
  );
}

function ValueItem({ label, value }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="font-heading text-xl font-semibold text-primary mt-1">R$ {value.toFixed(2)}</div>
    </div>
  );
}
